import {
  EventLevelEventTemplateId,
  type SingleMarketTemplate,
  type TemplateView
} from '@ps/web-sports-api-types';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { EventTemplate } from '@swf/sports/components/EventTemplate/EventTemplate';
import { MarketTemplate } from '@swf/sports/components/MarketTemplate/MarketTemplate';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';
import { isSingleMarketTemplate } from '@swf/sports/utils/isSingleMarketTemplate';

import styles from '../../GroupedTemplatePanels.scss';
import Header from '../Header/Header';

interface Props {
  groupInPanel: TemplateView[];
  marketId?: string;
}

const SingleMarketTemplateListItem: React.FC<{
  template: SingleMarketTemplate;
  shouldShowHeader: boolean;
  renderedMarketTypes: (string | undefined)[];
}> = ({ template, shouldShowHeader, renderedMarketTypes }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useSelector((state: RootState) => widgetSelect.market(state, template.marketId));

  if (!market) return null;

  renderedMarketTypes.unshift(market.marketType);
  const [currentMarketType, previousMarketType] = renderedMarketTypes;

  return (
    <>
      {shouldShowHeader || currentMarketType !== previousMarketType ? (
        <Header market={market} eventTypeId={market.eventTypeId} />
      ) : null}

      <MarketTemplate marketId={template.marketId} templateId={template.templateId} />
    </>
  );
};

const GroupInPanel: React.FC<Props> = ({ groupInPanel }) => {
  const renderedTemplateIds: string[] = [];
  const renderedMarketTypes: (string | undefined)[] = [];

  return (
    <div data-testid="event-list">
      <ol className={styles.list}>
        {groupInPanel.map(({ template }, index) => {
          renderedTemplateIds.unshift(template.templateId);
          const [currentTemplate, previousTemplate] = renderedTemplateIds;
          const isFirstTemplate = index === 0;
          const isDifferentTemplate = currentTemplate !== previousTemplate;

          if (isCompoundMarketTemplate(template)) {
            renderedMarketTypes.unshift(undefined);

            return (
              <li key={template.marketIds.join(',')}>
                <MarketTemplate
                  marketIds={template.marketIds}
                  templateId={template.templateId}
                  config={template.config}
                  templateDisplayConfig={{
                    EVENT_TABLE: {
                      hideHeader: !isDifferentTemplate && !isFirstTemplate
                    }
                  }}
                />
              </li>
            );
          }

          if (isSingleMarketTemplate(template)) {
            const shouldShowHeader =
              isFirstTemplate ||
              isDifferentTemplate ||
              previousTemplate === EventLevelEventTemplateId.EVENT_MORE_MARKETS;

            return (
              <li key={template.marketId}>
                <SingleMarketTemplateListItem
                  key={template.marketId}
                  template={template}
                  shouldShowHeader={shouldShowHeader}
                  renderedMarketTypes={renderedMarketTypes}
                />
              </li>
            );
          }

          renderedMarketTypes.unshift(undefined);

          return (
            <li key={template.eventId}>
              <EventTemplate eventId={template.eventId} templateId={template.templateId} />
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default GroupInPanel;
